import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { Marcacao } from "../../models/Marcacao";

import { LoggingService } from "../logging.service";
import { EnumTipoObjeto } from "src/app/enums/EnumTipoObjeto";
import { ErrorHandlerService } from "../errorHandler.service";
import { OperacaoSignalR } from "../../models/OperacaoSignalR";
import { SignalrService } from "../signalr.service";
import { StorageService } from "../storage.service";

@Injectable()
export class UsuarioMarcacoesService {
  readonly databaseName = "userdata";
  readonly collectionName = "marcacoes";

  public modificadoMarcacao$ = new Subject<Marcacao>();
  private modificadoMarcacao: Marcacao = null;

  constructor(
    private errorHandlerService: ErrorHandlerService,
    private signalrService: SignalrService,
    private loggingService: LoggingService,
    private storageService: StorageService
  ) {
    signalrService
      .getMensagem()
      .subscribe((msg) => this.onMessageReceived(msg));
  }

  public async fromNuvem(marcacoes: Marcacao[]): Promise<void> {
    return await this.storageService
      .upsertMany(marcacoes, this.databaseName, this.collectionName, false)
      .then(() => {})
      .catch((error) => {
        this.errorHandlerService.handleError(error);
      });
  }

  public async atualizar(marcacao: Marcacao, sync = true): Promise<Marcacao> {
    if (sync) {
      const mensagem = new OperacaoSignalR();
      mensagem.dados = marcacao;
      this.signalrService.enviarMensagem(mensagem, EnumTipoObjeto.Marcacoes);
    }

    return await this.storageService
      .upsert(marcacao, this.databaseName, this.collectionName)
      .then((marcacaoResult) => {
        const novo = !marcacaoResult.dataHoraModificacao;

        this.modificadoMarcacao = marcacaoResult;

        this.updateModificadoMarcacao();

        if (novo) {
          this.loggingService.logEvent(
            "Leitor - Marcar prova",
            marcacaoResult.idLei,
            marcacaoResult.id
          );
        }
        return marcacaoResult;
      })
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Marcacao();
      });
  }

  public async remover(marcacao: Marcacao): Promise<Marcacao> {
    marcacao.removido = true;
    return await this.atualizar(marcacao);
  }

  public async buscarLei(idLei: string): Promise<Marcacao[]> {
    return await this.storageService
      .list<Marcacao>(
        this.databaseName,
        this.collectionName,
        (m) => m.idLei === idLei
      )
      .then((marcacoes) => marcacoes)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Array<Marcacao>();
      });
  }

  public async listar(): Promise<Marcacao[]> {
    return await this.storageService
      .list<Marcacao>(this.databaseName, this.collectionName)
      .then((marcacoes) => marcacoes)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Array<Marcacao>();
      });
  }

  public async buscarLinhaVersao(
    idLinha: string,
    versaoLinha: number
  ): Promise<Marcacao[]> {
    return await this.storageService
      .list<Marcacao>(this.databaseName, this.collectionName, (m) => {
        m.range.idItens.forEach((id) => {
          if (id.idItem === idLinha && id.idImportacao === versaoLinha) {
            return true;
          }
        });

        return false;
      })
      .then((marcacoes) => marcacoes)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Array<Marcacao>();
      });
  }

  public async buscarLinha(idLinha: string): Promise<Marcacao[]> {
    return await this.storageService
      .list<Marcacao>(this.databaseName, this.collectionName, (m) => {
        m.range.idItens.forEach((id) => {
          if (id.idItem === idLinha) {
            return true;
          }
        });

        return false;
      })
      .then((marcacoes) => marcacoes)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Array<Marcacao>();
      });
  }

  updateModificadoMarcacao() {
    this.modificadoMarcacao$.next(this.modificadoMarcacao);
  }

  getModificadoMarcacao(): Observable<Marcacao> {
    return this.modificadoMarcacao$.asObservable();
  }

  private async onMessageReceived(msg: OperacaoSignalR): Promise<void> {
    if (msg && msg.tipoObjeto === EnumTipoObjeto.Marcacoes) {
      await this.atualizar(msg.dados, false);
    }
  }
}
